import { Link } from "react-router-dom";
import { Scale, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Button variant="ghost" asChild className="mb-6">
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </Button>

        <div className="flex items-center gap-3 mb-8">
          <Scale className="h-8 w-8 text-gold" />
          <h1 className="font-serif text-3xl font-bold">Privacy Policy</h1>
        </div>

        <div className="prose prose-invert max-w-none space-y-6 text-foreground">
          <p className="text-muted-foreground">
            Last updated: November 3, 2026
          </p>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">1. Information We Collect</h2>
            <p>We collect the following personal information when you create an account:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong className="text-foreground">Name:</strong> Your full legal name</li>
              <li><strong className="text-foreground">Email Address:</strong> For account access and communications</li>
              <li><strong className="text-foreground">Date of Birth:</strong> To verify age requirements</li>
              <li><strong className="text-foreground">Mailing Address:</strong> Street address, city, state, ZIP code, and country</li>
              <li><strong className="text-foreground">Phone Number:</strong> Optional, for account recovery and notifications</li>
              <li><strong className="text-foreground">Payment Information:</strong> Processed securely through Stripe</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">2. Automatically Collected Information</h2>
            <p>When you visit our website, we automatically collect:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong className="text-foreground">Device Information:</strong> Device type, browser, operating system</li>
              <li><strong className="text-foreground">Usage Data:</strong> Pages visited, time spent, clicks, and interactions</li>
              <li><strong className="text-foreground">IP Address:</strong> For security and approximate location</li>
              <li><strong className="text-foreground">Referral Source:</strong> The website, app, or link that brought you to us (for example, a social media post, search engine, or campaign link)</li>
              <li><strong className="text-foreground">Cookies:</strong> As described in our Cookie Policy</li>
            </ul>
            <p data-testid="privacy-preview-page-notice">
              <strong className="text-foreground">Public Preview Page.</strong> When you visit our free preview page
              (barsongz.com/preview), no account is required. We record your approximate location (country, region,
              and city, estimated from your IP address), device type, browser, operating system, referral source, and
              how you interact with the page (such as playing the sample song or viewing or downloading the Legal Lead
              Sheetz™). For page visits, we store a one-way scrambled (hashed) version of your IP address rather than
              the address itself, and we use this information only in aggregate to understand how the preview page is used.
              Approximate location is determined using IP geolocation data provided by DB-IP (db-ip.com).
              Lead Sheetz™ downloads are marked with a unique download ID, the download date, and barsongz.com. To
              protect our copyrighted materials, we keep a record of each download, including that ID, the date and
              time, your IP address, and your browser information. Sample audio on the preview page contains an
              inaudible ownership mark for the same purpose.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">3. How We Use Your Information</h2>
            <p>We use the collected information for:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Providing and improving our services</li>
              <li>Processing payments and subscriptions</li>
              <li>Communicating with you about your account</li>
              <li>Sending promotional materials (with your consent)</li>
              <li>Analytics and service optimization</li>
              <li>Fraud prevention and security</li>
              <li>Legal compliance</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">4. Information Sharing</h2>
            <p>We do not sell your personal information. We may share information with:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong className="text-foreground">Service Providers:</strong> Payment processors, hosting services, analytics providers</li>
              <li><strong className="text-foreground">Legal Requirements:</strong> When required by law or legal process</li>
              <li><strong className="text-foreground">Business Transfers:</strong> In connection with mergers or acquisitions</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">5. Data Security</h2>
            <p className="text-muted-foreground">
              We implement appropriate technical and organizational measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. This includes 
              encryption, secure servers, and regular security assessments.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Opt-out of marketing communications</li>
              <li>Data portability</li>
            </ul>
            <p className="text-muted-foreground">
              To exercise these rights, please contact us at privacy@barsongs.com
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">7. Children's Privacy</h2>
            <p className="text-muted-foreground">
              Our services are not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">8. Changes to This Policy</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">9. Contact Us</h2>
            <p className="text-muted-foreground">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-muted-foreground">
              Email: privacy@barsongs.com<br />
              Address: Bar Songz, Legal Department
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
